// Partner-link hooks — the MEMBER side of the partner view (ART-222/239; the UI says "partner", the code keeps
// "partner"). Band-scoped so the keys are ['band', bandId, 'partnerLinks']. The link is the member's ALONE (product
// owner, 2026-10-08): the read returns the caller's own link and their own pending invite, whatever their seat.
// Distinct from `usePartner.ts`, which is the PARTNER's own person-scoped read surface.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type PartnerLinksRead = components["schemas"]["PartnerLinksRead"];
export type PartnerLinkView = components["schemas"]["PartnerLinkView"];
export type PartnerPendingInvite = components["schemas"]["PartnerPendingInvite"];
export type PartnerInviteIssued = components["schemas"]["PartnerInviteIssued"];

const partnerKey = (bandId: string) => ["band", bandId, "partnerLinks"];

/** `GET /bands/{bandId}/partner-links` — the caller's own partner link + their own pending invite. */
export function usePartnerLinks() {
  const { api } = useApp();
  return useBandQuery<PartnerLinksRead>(
    ["partnerLinks"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/partner-links", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/** `POST /bands/{bandId}/partner-links/invite` — invite your partner by email, with the name to put on guest
 *  lists. Returns the `/join` link (also emailed) for the box to copy or share. */
export function useInvitePartner() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<PartnerInviteIssued, { email: string; alias?: string; relationship?: string }>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/partner-links/invite", {
        params: { path: { bandId: band } },
        body: { email: body.email, alias: body.alias || null, relationship: body.relationship || null },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: partnerKey(bandId) }) },
  );
}

/** `DELETE /bands/{bandId}/partner-links/{id}` — remove your partner (immediate; their view goes dark on their next
 *  request). Only the member may — `core.revoke_partner_link` enforces it. */
export function useRemovePartnerLink() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, string>(
    async (band, partnerLinkId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/partner-links/{partnerLinkId}", {
        params: { path: { bandId: band, partnerLinkId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: partnerKey(bandId) }) },
  );
}

/** `PUT /bands/{bandId}/partner-links/{id}/relationship` — set (or clear, with null) what kind of partner yours is.
 *  Only the member may — `core.set_partner_relationship` enforces it. */
export function useSetPartnerRelationship() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { partnerLinkId: string; relationship: string | null }>(
    async (band, { partnerLinkId, relationship }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/partner-links/{partnerLinkId}/relationship", {
        params: { path: { bandId: band, partnerLinkId } },
        body: { relationship },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: partnerKey(bandId) }) },
  );
}

/** `DELETE /bands/{bandId}/partner-links/invites/{id}` — cancel your own pending partner invite (the link dies). */
export function useCancelPartnerInvite() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, string>(
    async (band, inviteId) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/partner-links/invites/{inviteId}", {
        params: { path: { bandId: band, inviteId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: partnerKey(bandId) }) },
  );
}
